import { Assessment } from '@/common/types/assessment';
import {
  CreateCustomLevelInput,
  CustomLevel,
  CustomLevelStudentProgress,
  UpdateCustomLevelInput,
} from '@/common/types/customLevel';
import { Group } from '@/common/types/group';
import {
  CreateLearnerNoteInput,
  LearnerNote,
  UpdateLearnerNoteInput,
} from '@/common/types/learnerNote';
import {
  CreateLessonPlanInput,
  LessonPlan,
  UpdateLessonPlanInput,
} from '@/common/types/lessonPlan';
import { migrateAssessment } from '@/common/types/migrations/assessment';
import { migrateGameSettings } from '@/common/types/migrations/settings';
import {
  BillingInfo,
  Coupon,
  Invitation,
  Organisation,
  OrganisationLicense,
} from '@/common/types/organisation';
import {
  PlacementTest,
  PlacementTestResult,
} from '@/common/types/placementTest';
import {
  Activity,
  Attempt,
  CreateActivity,
  CreateAttempt,
  Session,
} from '@/common/types/session';
import { ShareLink, ShareLinkType } from '@/common/types/shareLink';
import {
  ControllerType,
  GameSettings,
  LevelProgress,
  Student,
} from '@/common/types/student';
import {
  CreateStudentEvent,
  StudentEvent,
  StudentEventType,
} from '@/common/types/studentEvents';
import { OnboardingInfo, User } from '@/common/types/user';
import {
  CreateWordMatInput,
  UpdateWordMatInput,
  WordMat,
} from '@/common/types/wordMat';
import { deepMerge } from '@/common/utils/deepMerge';
import config from '@/db/firebase-config';
import { initializeApp } from 'firebase/app';
import {
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
} from 'firebase/app-check';
import {
  Auth,
  connectAuthEmulator,
  getAuth,
  signInWithCustomToken,
  signOut,
} from 'firebase/auth';
import {
  addDoc,
  arrayRemove,
  arrayUnion,
  collection,
  connectFirestoreEmulator,
  deleteDoc,
  doc,
  documentId,
  DocumentReference,
  Firestore,
  getDoc,
  getDocs,
  getFirestore,
  increment,
  limit,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import {
  connectFunctionsEmulator,
  Functions,
  getFunctions,
  httpsCallable,
} from 'firebase/functions';
import { chunk } from '../common/utils/helpers';

class FirebaseService {
  public readonly db: Firestore;
  public readonly auth: Auth;
  public readonly functions: Functions;

  constructor(appName?: string) {
    const app = initializeApp(config, appName);

    const db = getFirestore(app);
    const auth = getAuth(app);
    const functions = getFunctions(app);

    const isDevelopmentServer = import.meta.env.MODE === 'development';
    const isDeployPreview = window.location.hostname.includes('deploy-preview');

    if (isDevelopmentServer) {
      console.info('Using Firebase Emulators');
      connectFirestoreEmulator(db, 'localhost', 8080);
      connectAuthEmulator(auth, 'http://127.0.0.1:9099/', {
        disableWarnings: true,
      });
      connectFunctionsEmulator(functions, 'localhost', 5001);
    } else if (!isDeployPreview) {
      // Initialise App Check
      const reCaptchaEnterpriseSiteKey = import.meta.env
        .VITE_RECAPTCHA_ENTERPRISE_SITE_KEY;
      if (!reCaptchaEnterpriseSiteKey) {
        throw new Error(
          'reCAPTCHA Enterprise site key environment variable is missing.',
        );
      }
      initializeAppCheck(app, {
        provider: new ReCaptchaEnterpriseProvider(reCaptchaEnterpriseSiteKey),
        isTokenAutoRefreshEnabled: true,
      });
    }

    this.db = db;
    this.auth = auth;
    this.functions = functions;
  }

  async getFirstInviteTokenForEmail(email: string): Promise<string | null> {
    try {
      const inviteQuery = query(
        collection(this.db, 'invites'),
        where('email', '==', email),
        orderBy('createdAt', 'desc'),
        limit(1),
      );

      const inviteSnapshot = await getDocs(inviteQuery);

      if (inviteSnapshot.empty) {
        return null;
      }

      return inviteSnapshot.docs[0].id;
    } catch (e) {
      console.error('Error getting first invite token for email:', e);
      return null;
    }
  }

  async fetchInviteDetails(token: string) {
    const inviteRef = doc(this.db, 'invites', token);
    const inviteDoc = await getDoc(inviteRef);

    if (!inviteDoc.exists()) {
      throw new Error('Invite not found. Please check the link and try again.');
    }

    const invite = { token, ...inviteDoc.data() } as Invitation;

    return invite;
  }

  async respondToInvite(
    token: string,
    accept: boolean,
  ): Promise<{ success: boolean; message?: string }> {
    const params = { token, accept };
    const respondToInviteFn = httpsCallable(this.functions, 'respondToInvite');
    const result = await respondToInviteFn(params);
    return result.data as {
      success: boolean;
      message?: string;
    };
  }

  async getPendingInvitesForOrganisation(orgId: string) {
    try {
      const invitesQuery = query(
        collection(this.db, 'invites'),
        where('organisationId', '==', orgId),
      );
      const invitesSnapshot = await getDocs(invitesQuery);

      const invites: Invitation[] = invitesSnapshot.docs.map(
        (doc) => ({ token: doc.id, ...doc.data() }) as Invitation,
      );

      return invites;
    } catch (error) {
      console.error('Error fetching invites:', error);
      throw error;
    }
  }

  async deleteInvite(token: string) {
    try {
      const inviteRef = doc(this.db, 'invites', token);
      await deleteDoc(inviteRef);
    } catch (error) {
      console.error('Error deleting invite:', error);
      throw error;
    }
  }

  async removeMemberFromOrganisation(organisationId: string, userId: string) {
    try {
      const orgDocRef = doc(this.db, 'organisations', organisationId);
      const userDocRef = doc(this.db, 'users', userId);

      await runTransaction(this.db, async (tx) => {
        const orgSnap = await tx.get(orgDocRef);
        if (!orgSnap.exists()) {
          throw new Error(`Organisation ${organisationId} not found`);
        }

        const data = orgSnap.data() as Organisation;
        const members = Array.isArray(data.members) ? data.members : [];

        tx.update(orgDocRef, {
          members: members.filter((m) => m.uid !== userId),
          memberIds: members.map((m) => m.uid),
          updatedAt: Date.now(),
        });
        tx.update(userDocRef, {
          organisationIds: arrayRemove(organisationId),
          updatedAt: Date.now(),
        });
      });
    } catch (error) {
      console.error('Error removing member from organisation:', error);
      throw error;
    }
  }

  async getPendingInvitesForEmail(email: string): Promise<Invitation[]> {
    const invitesQuery = query(
      collection(this.db, 'invites'),
      where('email', '==', email),
      orderBy('createdAt', 'desc'),
    );

    const inviteSnapshot = await getDocs(invitesQuery);
    return inviteSnapshot.docs.map(
      (doc) =>
        ({
          token: doc.id,
          ...doc.data(),
        }) as Invitation,
    );
  }

  async changeOrganisationMemberRole(
    organisationId: string,
    userId: string,
    role: 'admin' | 'member',
  ) {
    try {
      const orgDocRef = doc(this.db, 'organisations', organisationId);

      await runTransaction(this.db, async (tx) => {
        const orgSnap = await tx.get(orgDocRef);
        if (!orgSnap.exists()) {
          throw new Error(`Organisation ${organisationId} not found`);
        }

        const data = orgSnap.data() as Organisation;
        const members = Array.isArray(data.members) ? data.members : [];

        tx.update(orgDocRef, {
          members: members.map((m) => (m.uid === userId ? { ...m, role } : m)),
          updatedAt: Date.now(),
        });
      });
    } catch (error) {
      console.error('Error changing organisation member role:', error);
      throw error;
    }
  }

  async subscribeToUser(userId: string, callback: (user: User) => void) {
    const userDocRef = doc(this.db, 'users', userId);
    return onSnapshot(
      userDocRef,
      (doc) => {
        const data = doc.data();
        if (data) {
          callback(data as User);
        }
      },
      (error) => {
        console.error('Error in user subscription:', error);
      },
    );
  }

  async subscribeToOrganisation(
    organisationId: string,
    callback: (organisation: Organisation) => void,
  ) {
    const orgDocRef = doc(this.db, 'organisations', organisationId);
    return onSnapshot(
      orgDocRef,
      (doc) => {
        const data = doc.data();
        if (data) {
          callback({
            id: doc.id,
            ...data,
          } as Organisation);
        }
      },
      (error) => {
        console.error('Error in organisation subscription:', error);
      },
    );
  }

  async getOrganisations(organisationIds: string[]): Promise<Organisation[]> {
    if (!organisationIds.length) return [];
    try {
      const orgsQuery = query(
        collection(this.db, 'organisations'),
        where(documentId(), 'in', organisationIds),
      );
      const orgsSnapshot = await getDocs(orgsQuery);

      const organisations: Organisation[] = orgsSnapshot.docs.map(
        (doc) =>
          ({
            id: doc.id,
            ...doc.data(),
          }) as Organisation,
      );

      // Make sure settings are up to date
      return await Promise.all(
        organisations.map(async (org) => {
          const updatedSettings = await migrateGameSettings(
            org.defaultStudentSettings,
          );
          return {
            ...org,
            defaultStudentSettings: updatedSettings,
          };
        }),
      );
    } catch (error) {
      console.error('Error getting organisations:', error);
      throw error;
    }
  }

  async getOrganisation(
    organisationId: string,
  ): Promise<Organisation | undefined> {
    try {
      const orgDocRef = doc(this.db, 'organisations', organisationId);
      const orgDocSnapshot = await getDoc(orgDocRef);
      if (!orgDocSnapshot.exists()) return undefined;

      const organisation = {
        id: orgDocSnapshot.id,
        ...orgDocSnapshot.data(),
      } as Organisation;

      const updatedSettings = await migrateGameSettings(
        organisation.defaultStudentSettings,
      );

      return {
        ...organisation,
        defaultStudentSettings: updatedSettings,
      };
    } catch (error) {
      if ((error as { code?: string }).code !== 'permission-denied') {
        console.error('Error fetching organisation:', error);
      }
      return undefined;
    }
  }

  async createOrganisation(params: {
    organisationName: string;
    stripeCustomerId: string;
  }): Promise<{ organisationId: string }> {
    const createOrganisationFn = httpsCallable<
      typeof params,
      { organisationId: string }
    >(this.functions, 'createOrganisation');
    const result = await createOrganisationFn(params);
    return result.data;
  }

  async updateOrganisationName(organisationId: string, name: string) {
    try {
      const orgDocRef = doc(this.db, 'organisations', organisationId);
      await updateDoc(orgDocRef, {
        name,
        updatedAt: Date.now(),
      });
    } catch (e) {
      console.error('Error updating organisation name:', e);
    }
  }

  async updateOrganisationDefaultSettings(
    orgId: string,
    updates: Partial<GameSettings>,
  ) {
    try {
      const orgDocRef = doc(this.db, 'organisations', orgId);
      const orgData = (await getDoc(orgDocRef)).data() as Organisation;

      const updatedSettings = deepMerge(
        orgData.defaultStudentSettings,
        updates,
      );

      await updateDoc(orgDocRef, {
        defaultStudentSettings: updatedSettings,
        updatedAt: Date.now(),
      });
    } catch (e) {
      console.error('Error updating organisation default settings:', e);
      throw e;
    }
  }

  async markOrganisationHasViewedResults(orgId: string) {
    const orgDocRef = doc(this.db, 'organisations', orgId);
    await updateDoc(orgDocRef, {
      'activationChecklist.hasViewedResults': true,
      updatedAt: Date.now(),
    });
  }

  async markActivationChecklistDismissed(orgId: string) {
    const orgDocRef = doc(this.db, 'organisations', orgId);
    await updateDoc(orgDocRef, {
      'activationChecklist.isDismissed': true,
      updatedAt: Date.now(),
    });
  }

  async resetOrganisationLicense(orgId: string) {
    try {
      const orgDocRef = doc(this.db, 'organisations', orgId);
      const orgData = (await getDoc(orgDocRef)).data() as Organisation;
      await updateDoc(orgDocRef, {
        license: {
          ...orgData.license,
          plan: 'trial',
          status: 'active',
          billingCycle: '1year',
          billingCycleAnchor: Date.now(),
          stripeSubscriptionId: null,
          seatCount: 0,
          scheduledForCancellation: false,
          coupon: null,
        } satisfies OrganisationLicense,
        updatedAt: Date.now(),
      });
    } catch (e) {
      console.error('Error resetting organisation license:', e);
      throw e;
    }
  }

  async createGroup(group: Omit<Group, 'id'>) {
    try {
      const groupDocRef = await addDoc(collection(this.db, 'groups'), group);
      return groupDocRef.id;
    } catch (e) {
      console.error('Error creating group:', e);
      throw e;
    }
  }

  async getGroupsForOrganisation(orgId: string) {
    try {
      const groupsQuery = query(
        collection(this.db, 'groups'),
        where('organisationId', '==', orgId),
      );
      const groupsSnapshot = await getDocs(groupsQuery);

      const groups: Group[] = groupsSnapshot.docs.map(
        (doc) =>
          ({
            id: doc.id,
            ...doc.data(),
          }) as Group,
      );

      return groups;
    } catch (error) {
      console.error('Error fetching groups for organisation:', error);
      throw error;
    }
  }

  async updateGroup(groupId: string, updates: Partial<Group>) {
    try {
      const groupDocRef = doc(this.db, 'groups', groupId);
      await updateDoc(groupDocRef, {
        ...updates,
        updatedAt: Date.now(),
      });
    } catch (error) {
      console.error('Error updating group:', error);
      throw error;
    }
  }

  async deleteGroup(groupId: string) {
    try {
      const groupDocRef = doc(this.db, 'groups', groupId);
      await deleteDoc(groupDocRef);
    } catch (error) {
      console.error('Error deleting group:', error);
      throw error;
    }
  }

  async createUserProfile(user: User) {
    try {
      await setDoc(doc(this.db, 'users', user.uid), user);
      return user.uid;
    } catch (error) {
      console.error('Error creating user profile:', error);
      throw error;
    }
  }

  async updateUserName(uid: string, name: string) {
    try {
      const userRef = doc(this.db, 'users', uid);
      const now = Date.now();

      await runTransaction(this.db, async (tx) => {
        const userSnap = await tx.get(userRef);
        if (!userSnap.exists()) {
          throw new Error(`User not found: ${uid}`);
        }

        const user = userSnap.data() as User;
        const organisationIds = Array.isArray(user.organisationIds)
          ? user.organisationIds
          : [];

        const orgs: [DocumentReference, Organisation][] = [];
        for (const orgId of organisationIds) {
          const orgRef = doc(this.db, 'organisations', orgId);
          const orgSnap = await tx.get(orgRef);
          if (!orgSnap.exists()) {
            console.error(`Organisation (${orgId}) not found`);
            continue;
          }
          orgs.push([orgRef, orgSnap.data() as Organisation]);
        }

        tx.update(userRef, {
          name,
          updatedAt: now,
        });

        for (const [ref, org] of orgs) {
          tx.update(ref, {
            members: org.members.map((m) =>
              m.uid === uid ? { ...m, name } : m,
            ),
            updatedAt: now,
          });
        }
      });
    } catch (error) {
      console.error('Error updating user name:', error);
      throw error;
    }
  }

  async updateUserMarketingOptin(uid: string, optInStatus: boolean) {
    try {
      const userRef = doc(this.db, 'users', uid);
      await updateDoc(userRef, {
        marketingOptIn: optInStatus,
        updatedAt: Date.now(),
      });
    } catch (error) {
      console.error('Error updating marketing opt-in status:', error);
      throw error;
    }
  }

  async markUserOnboardingCompleted(userId: string) {
    try {
      const userRef = doc(this.db, 'users', userId);
      await updateDoc(userRef, {
        onboardingCompleted: true,
        updatedAt: Date.now(),
      });
    } catch (error) {
      console.error('Error updating marketing opt-in status:', error);
      throw error;
    }
  }

  async markTourViewed(uid: string, tourKey: string) {
    const userRef = doc(this.db, 'users', uid);
    await updateDoc(userRef, {
      viewedTours: arrayUnion(tourKey),
      updatedAt: Date.now(),
    });
  }

  async getUserProfile(uid: string): Promise<User | undefined> {
    try {
      const docRef = doc(this.db, 'users', uid);
      const docSnapshot = await getDoc(docRef);

      if (!docSnapshot.exists()) return;
      return docSnapshot.data() as User;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      throw error;
    }
  }

  async updateOnboardingInfo(
    uid: string,
    completed: boolean,
    onboardingInfo: OnboardingInfo,
  ) {
    try {
      const userDocRef = doc(this.db, 'users', uid);
      await updateDoc(userDocRef, {
        onboardingCompleted: completed,
        onboardingInfo,
        updatedAt: Date.now(),
      });
    } catch (error) {
      console.error('Error completing onboarding for user:', error);
      throw error;
    }
  }

  async getStudentsForOrganisation(orgId: string) {
    try {
      const studentsQuery = query(
        collection(this.db, 'students'),
        where('organisationId', '==', orgId),
      );
      const studentsSnapshot = await getDocs(studentsQuery);

      const students: Student[] = studentsSnapshot.docs.map(
        (doc) =>
          ({
            id: doc.id,
            ...doc.data(),
          }) as Student,
      );

      return students;
    } catch (error) {
      console.error('Error fetching students for organisation:', error);
      throw error;
    }
  }

  async getStudent(studentId: string) {
    const docRef = doc(this.db, 'students', studentId);

    const docSnapshot = await getDoc(docRef);
    if (!docSnapshot.exists()) {
      throw new Error('No student found with the provided ID');
    }

    return {
      id: docSnapshot.id,
      ...docSnapshot.data(),
    } as Student;
  }

  async getSession(sessionId: string) {
    const sessionDocRef = doc(this.db, 'sessions', sessionId);
    const sessionSnapshot = await getDoc(sessionDocRef);

    if (!sessionSnapshot.exists) {
      throw new Error(`Session (id: ${sessionId}) not found`);
    }

    return {
      id: sessionSnapshot.id,
      ...sessionSnapshot.data(),
    } as Session;
  }

  async getPlacementTestResults(organisationId: string, sessionId: string) {
    const resultsQuery = query(
      collection(this.db, 'placementTestResults'),
      where('organisationId', '==', organisationId),
      where('sessionId', '==', sessionId),
      orderBy('startedAt', 'desc'),
    );
    const resultsSnapshot = await getDocs(resultsQuery);
    return resultsSnapshot.docs.map(
      (doc) =>
        ({
          id: doc.id,
          ...doc.data(),
        }) as PlacementTestResult,
    );
  }

  async getAttemptsForActivity(
    organisationId: string,
    activityId: string,
  ): Promise<Attempt[]> {
    const attemptsQuery = query(
      collection(this.db, 'attempts'),
      where('organisationId', '==', organisationId),
      where('activityId', '==', activityId),
      orderBy('startedAt', 'asc'),
    );
    const attemptsSnapshot = await getDocs(attemptsQuery);
    if (attemptsSnapshot.empty) return [];

    const attempts: Attempt[] = [];
    attemptsSnapshot.docs.forEach((doc) => {
      const attemptData = doc.data();
      if (attemptData.attempts) {
        // old structure
        for (let i = 0; i < attemptData.attempts.length; i++) {
          const attempt = attemptData.attempts[i];
          // include index in id to avoid duplicates
          attempts.push({ id: `${doc.id}_${i}`, ...attempt } as Attempt);
        }
      } else {
        attempts.push({ id: doc.id, ...attemptData } as Attempt);
      }
    });

    return attempts;
  }

  async getAttemptsForStudent(
    organisationId: string,
    studentId: string,
  ): Promise<Attempt[]> {
    const attemptsQuery = query(
      collection(this.db, 'attempts'),
      where('organisationId', '==', organisationId),
      where('studentId', '==', studentId),
      orderBy('startedAt', 'asc'),
    );
    const attemptsSnapshot = await getDocs(attemptsQuery);
    return attemptsSnapshot.docs.map(
      (doc) =>
        ({
          id: doc.id,
          ...doc.data(),
        }) as Attempt,
    );
  }

  async isUsernameAvailable(username: string): Promise<boolean> {
    const fn = httpsCallable<{ username: string }, { available: boolean }>(
      this.functions,
      'isUsernameAvailable',
    );
    const result = await fn({ username });
    return result.data.available;
  }

  async createNewStudent(data: Omit<Student, 'id'>): Promise<string> {
    try {
      const studentDocRef = await addDoc(collection(this.db, 'students'), data);

      // increment org student count
      const orgRef = doc(this.db, 'organisations', data.organisationId);
      await updateDoc(orgRef, {
        studentCount: increment(1),
        updatedAt: Date.now(),
      });

      console.info('Document written with ID: ', studentDocRef.id);
      return studentDocRef.id; // Return the newly created document ID
    } catch (e) {
      console.error('Error adding document: ', e);
      throw e; // Throw the error to be caught by the caller
    }
  }

  async updateStudentDetails(
    studentId: string,
    data: Partial<Student>,
  ): Promise<void> {
    try {
      const studentDocRef = doc(this.db, 'students', studentId);
      await updateDoc(studentDocRef, {
        ...data,
        updatedAt: Date.now(),
      });
    } catch (e) {
      console.error('Error updating document: ', e);
      throw e; // Throw the error to be caught by the caller
    }
  }

  async deleteStudent(
    studentId: string,
    organisationId: string,
  ): Promise<{ deletedAssessmentIds: string[] }> {
    const studentDocRef = doc(this.db, 'students', studentId);

    try {
      await deleteDoc(studentDocRef);

      // remove studentId from groups
      const groupDocQuery = query(
        collection(this.db, 'groups'),
        where('organisationId', '==', organisationId),
        where('studentIds', 'array-contains', studentId),
      );
      const groupDocsSnapshot = await getDocs(groupDocQuery);
      for (const groupDoc of groupDocsSnapshot.docs) {
        await updateDoc(groupDoc.ref, {
          studentIds: arrayRemove(studentId),
          updatedAt: Date.now(),
        });
      }

      // remove student from organisation
      const orgRef = doc(this.db, 'organisations', organisationId);
      await updateDoc(orgRef, {
        studentCount: increment(-1),
        updatedAt: Date.now(),
      });

      // remove student from assessments
      const deletedAssessmentIds: string[] = [];
      const assessmentsQuery = query(
        collection(this.db, 'assessments'),
        where('organisationId', '==', organisationId),
        where('studentIds', 'array-contains', studentId),
      );
      const assessmentsSnapshot = await getDocs(assessmentsQuery);
      for (const doc of assessmentsSnapshot.docs) {
        const assessmentData = doc.data() as Omit<Assessment, 'id'>;
        const updatedStudents = { ...assessmentData.students };
        delete updatedStudents[studentId];
        if (Object.keys(updatedStudents).length === 0) {
          // was the only student - safe to delete assessment
          await deleteDoc(doc.ref);
          deletedAssessmentIds.push(doc.id);
        } else {
          // still other students - just remove this one
          await updateDoc(doc.ref, {
            students: updatedStudents,
            studentIds: arrayRemove(studentId),
            updatedAt: Date.now(),
          });
        }
      }

      // delete placment test results
      const placementTestResultsQuery = query(
        collection(this.db, 'placementTestResults'),
        where('organisationId', '==', organisationId),
        where('studentId', '==', studentId),
      );
      const placementTestResultsSnapshot = await getDocs(
        placementTestResultsQuery,
      );
      for (const doc of placementTestResultsSnapshot.docs) {
        await deleteDoc(doc.ref);
      }

      // delete placement tests
      const placementTestsQuery = query(
        collection(this.db, 'placementTests'),
        where('organisationId', '==', organisationId),
        where('studentId', '==', studentId),
      );
      const placementTestsSnapshot = await getDocs(placementTestsQuery);
      for (const doc of placementTestsSnapshot.docs) {
        await deleteDoc(doc.ref);
      }

      console.info('Document with ID: ', studentId, ' has been deleted.');
      return { deletedAssessmentIds };
    } catch (e) {
      console.error('Error deleting document: ', e);
      throw e;
    }
  }

  async createSession(organisationId: string, studentId: string) {
    const sessionsCollectionRef = collection(this.db, 'sessions');
    const startedAt = Date.now();

    const sessionData: Omit<Session, 'id'> = {
      organisationId,
      studentId,
      startedAt,
      activityCount: 0,
    };

    const docRef = await addDoc(sessionsCollectionRef, sessionData);

    await this.addStudentEvent({
      organisationId: organisationId,
      studentId: studentId,
      createdAt: startedAt,
      type: StudentEventType.SESSION_STARTED,
      sessionId: docRef.id,
      actor: { type: 'system' },
      nestedUnderSessionId: null,
    });

    return {
      id: docRef.id,
      ...sessionData,
    } as Session;
  }

  async incrementActivityCountForSession(sessionId: string) {
    const sessionDocRef = doc(this.db, 'sessions', sessionId);

    await runTransaction(this.db, async (transaction) => {
      const sessionSnapshot = await transaction.get(sessionDocRef);
      if (!sessionSnapshot.exists()) {
        throw new Error(`Session does not exist: ${sessionId}`);
      }
      const currentActivityCount = sessionSnapshot.data()?.activityCount || 0;
      transaction.update(sessionDocRef, {
        activityCount: currentActivityCount + 1,
      });
    });
  }

  async createActivity(activity: Omit<CreateActivity, 'id'>) {
    const activitiesCollectionRef = collection(this.db, 'activities');

    const docRef = await addDoc(activitiesCollectionRef, activity);

    return {
      id: docRef.id,
      ...activity,
    } as CreateActivity;
  }

  async updateActivityAccuracy(activityId: string, accuracy: number) {
    const activityDocRef = doc(this.db, 'activities', activityId);

    await runTransaction(this.db, async (transaction) => {
      const activitySnapshot = await transaction.get(activityDocRef);
      if (!activitySnapshot.exists()) {
        throw new Error('Activity does not exist!');
      }

      transaction.update(activityDocRef, {
        accuracy,
      });
    });

    return activityDocRef.id;
  }

  async updateActivityControlTypesUsed(
    activityId: string,
    controlTypesUsed: ControllerType[],
  ) {
    const activityDocRef = doc(this.db, 'activities', activityId);

    await runTransaction(this.db, async (transaction) => {
      const activitySnapshot = await transaction.get(activityDocRef);
      if (!activitySnapshot.exists()) {
        throw new Error('Activity does not exist!');
      }

      transaction.update(activityDocRef, {
        controlTypesUsed,
      });
    });

    return activityDocRef.id;
  }

  async endActivity(activityId: string) {
    const activityDocRef = doc(this.db, 'activities', activityId);

    const endedAt = Date.now();

    await updateDoc(activityDocRef, {
      endedAt,
    });

    return endedAt;
  }

  async getActivitiesForSession(organisationId: string, sessionId: string) {
    const activitiesQuery = query(
      collection(this.db, 'activities'),
      where('organisationId', '==', organisationId),
      where('sessionId', '==', sessionId),
      orderBy('startedAt', 'desc'),
    );
    const activitiesSnapshot = await getDocs(activitiesQuery);
    return activitiesSnapshot.docs.map(
      (doc) =>
        ({
          id: doc.id,
          ...doc.data(),
        }) as Activity,
    );
  }

  async getActivitiesForAssessment(
    organisationId: string,
    assessmentId: string,
  ) {
    const activitiesQuery = query(
      collection(this.db, 'activities'),
      where('organisationId', '==', organisationId),
      where('assessmentId', '==', assessmentId),
      orderBy('startedAt', 'desc'),
    );
    const activitiesSnapshot = await getDocs(activitiesQuery);
    return activitiesSnapshot.docs.map(
      (doc) =>
        ({
          id: doc.id,
          ...doc.data(),
        }) as Activity,
    );
  }

  async createAttempt(attempt: CreateAttempt) {
    const attemptCollectionDocRef = collection(this.db, 'attempts');

    const attemptDocRef = doc(attemptCollectionDocRef);
    await setDoc(attemptDocRef, attempt);
    return attemptDocRef.id;
  }

  async getOrCreateShareLink(
    organisationId: string,
    studentId: string,
    type: ShareLinkType,
    resourceId: string,
  ): Promise<ShareLink> {
    const q = query(
      collection(this.db, 'shareLinks'),
      where('organisationId', '==', organisationId),
      where('studentId', '==', studentId),
      where('type', '==', type),
      where('resourceId', '==', resourceId),
      where('expiresAt', '>', Date.now()),
      limit(1),
    );

    const snapshot = await getDocs(q);

    if (!snapshot.empty) {
      const doc = snapshot.docs[0];
      return { id: doc.id, ...doc.data() } as ShareLink;
    }

    // Link doesn't exist, need to create it
    return await this.createShareLink(
      organisationId,
      studentId,
      type,
      resourceId,
    );
  }

  async getShareLinkById(id: string): Promise<ShareLink | null> {
    const snap = await getDoc(doc(this.db, 'shareLinks', id));
    const shareLink = { id: snap.id, ...snap.data() } as ShareLink;
    if (shareLink.expiresAt < Date.now()) return null;
    return shareLink;
  }

  async createShareLink(
    organisationId: string,
    studentId: string,
    type: ShareLinkType,
    resourceId: string,
    expiryDuration: number = 1000 * 60 * 60 * 24 * 7, // 7 days
  ): Promise<ShareLink> {
    const now = Date.now();
    const shareLink: Omit<ShareLink, 'id'> = {
      createdAt: now,
      updatedAt: now,
      expiresAt: now + expiryDuration,
      organisationId,
      studentId,
      type,
      resourceId,
    };

    const shareLinksRef = collection(this.db, 'shareLinks');
    const shareLinkDocRef = await addDoc(shareLinksRef, shareLink);
    return { id: shareLinkDocRef.id, ...shareLink };
  }

  async deleteShareLink(id: string) {
    await deleteDoc(doc(this.db, 'shareLinks', id));
  }

  async getAssessmentsForOrganisation(orgId: string) {
    const assessmentsQuery = query(
      collection(this.db, 'assessments'),
      where('organisationId', '==', orgId),
    );
    const assessmentsSnapshot = await getDocs(assessmentsQuery);
    return await Promise.all(
      assessmentsSnapshot.docs.map((doc) =>
        migrateAssessment({
          id: doc.id,
          ...doc.data(),
        } as Assessment),
      ),
    );
  }

  async getAssessmentsForStudent(organisationId: string, studentId: string) {
    const assessmentsQuery = query(
      collection(this.db, 'assessments'),
      where('organisationId', '==', organisationId),
      where('studentIds', 'array-contains', studentId),
    );
    const assessmentsSnapshot = await getDocs(assessmentsQuery);
    return await Promise.all(
      assessmentsSnapshot.docs.map((doc) =>
        migrateAssessment({
          id: doc.id,
          ...doc.data(),
        } as Assessment),
      ),
    );
  }

  async getCustomLevel(customLevelId: string): Promise<CustomLevel | null> {
    const docRef = doc(this.db, 'customLevels', customLevelId);
    const snap = await getDoc(docRef);
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() } as CustomLevel;
  }

  async getAssessment(assessmentId: string): Promise<Assessment | null> {
    const assessmentDocRef = doc(this.db, 'assessments', assessmentId);
    const assessmentDoc = await getDoc(assessmentDocRef);
    if (!assessmentDoc.exists()) return null;
    return migrateAssessment({
      id: assessmentDoc.id,
      ...assessmentDoc.data(),
    } as Assessment);
  }

  async createAssessment(assessment: Omit<Assessment, 'id'>) {
    const assessmentsCollectionRef = collection(this.db, 'assessments');
    const assessmentDocRef = await addDoc(assessmentsCollectionRef, assessment);
    return assessmentDocRef.id;
  }

  async updateAssessment(assessmentId: string, updates: Partial<Assessment>) {
    const assessmentDocRef = doc(this.db, 'assessments', assessmentId);

    await updateDoc(assessmentDocRef, {
      ...updates,
      updatedAt: Date.now(),
    });
  }

  async deleteAssessment(assessmentId: string) {
    const assessmentDocRef = doc(this.db, 'assessments', assessmentId);
    await deleteDoc(assessmentDocRef);
  }

  async getWordMatsForOrganisation(orgId: string): Promise<WordMat[]> {
    const wordMatsQuery = query(
      collection(this.db, 'wordMats'),
      where('organisationId', '==', orgId),
      orderBy('updatedAt', 'desc'),
    );
    const wordMatsSnapshot = await getDocs(wordMatsQuery);
    return wordMatsSnapshot.docs.map(
      (doc) =>
        ({
          id: doc.id,
          ...doc.data(),
        }) as WordMat,
    );
  }

  async createWordMat(data: CreateWordMatInput): Promise<WordMat> {
    const wordMatsCollectionRef = collection(this.db, 'wordMats');
    const now = Date.now();
    const wordMatData = {
      ...data,
      createdAt: now,
      updatedAt: now,
    };
    const wordMatDocRef = await addDoc(wordMatsCollectionRef, wordMatData);
    return {
      id: wordMatDocRef.id,
      ...wordMatData,
    };
  }

  async updateWordMat(
    wordMatId: string,
    updates: UpdateWordMatInput,
  ): Promise<void> {
    const wordMatDocRef = doc(this.db, 'wordMats', wordMatId);
    await updateDoc(wordMatDocRef, {
      ...updates,
      updatedAt: Date.now(),
    });
  }

  async deleteWordMat(wordMatId: string): Promise<void> {
    const wordMatDocRef = doc(this.db, 'wordMats', wordMatId);
    await deleteDoc(wordMatDocRef);
  }

  // Lesson Plan methods
  async getLessonPlansForOrganisation(orgId: string): Promise<LessonPlan[]> {
    const lessonPlansQuery = query(
      collection(this.db, 'lessonPlans'),
      where('organisationId', '==', orgId),
      orderBy('updatedAt', 'desc'),
    );
    const lessonPlansSnapshot = await getDocs(lessonPlansQuery);
    return lessonPlansSnapshot.docs.map(
      (doc) =>
        ({
          id: doc.id,
          ...doc.data(),
        }) as LessonPlan,
    );
  }

  async getLessonPlan(lessonPlanId: string): Promise<LessonPlan | null> {
    const lessonPlanDocRef = doc(this.db, 'lessonPlans', lessonPlanId);
    const lessonPlanDoc = await getDoc(lessonPlanDocRef);
    if (!lessonPlanDoc.exists()) return null;
    return {
      id: lessonPlanDoc.id,
      ...lessonPlanDoc.data(),
    } as LessonPlan;
  }

  async createLessonPlan(data: CreateLessonPlanInput): Promise<LessonPlan> {
    const lessonPlansCollectionRef = collection(this.db, 'lessonPlans');
    const now = Date.now();
    const lessonPlanData = {
      ...data,
      createdAt: now,
      updatedAt: now,
    };
    const lessonPlanDocRef = await addDoc(
      lessonPlansCollectionRef,
      lessonPlanData,
    );
    return {
      id: lessonPlanDocRef.id,
      ...lessonPlanData,
    };
  }

  async updateLessonPlan(
    lessonPlanId: string,
    updates: UpdateLessonPlanInput,
  ): Promise<void> {
    const lessonPlanDocRef = doc(this.db, 'lessonPlans', lessonPlanId);
    await updateDoc(lessonPlanDocRef, {
      ...updates,
      updatedAt: Date.now(),
    });
  }

  async deleteLessonPlan(lessonPlanId: string): Promise<void> {
    const lessonPlanDocRef = doc(this.db, 'lessonPlans', lessonPlanId);
    await deleteDoc(lessonPlanDocRef);
  }

  // Learner Note methods
  async getLearnerNotesForStudent(
    orgId: string,
    studentId: string,
  ): Promise<LearnerNote[]> {
    const notesQuery = query(
      collection(this.db, 'learnerNotes'),
      where('organisationId', '==', orgId),
      where('studentId', '==', studentId),
      orderBy('updatedAt', 'desc'),
    );
    const notesSnapshot = await getDocs(notesQuery);
    return notesSnapshot.docs.map(
      (doc) =>
        ({
          id: doc.id,
          ...doc.data(),
        }) as LearnerNote,
    );
  }

  async getLearnerNote(id: string): Promise<LearnerNote | null> {
    const noteDocRef = doc(this.db, 'learnerNotes', id);
    const noteDoc = await getDoc(noteDocRef);
    if (!noteDoc.exists()) return null;
    return {
      id: noteDoc.id,
      ...noteDoc.data(),
    } as LearnerNote;
  }

  async createLearnerNote(data: CreateLearnerNoteInput): Promise<LearnerNote> {
    const notesCollectionRef = collection(this.db, 'learnerNotes');
    const now = Date.now();
    const noteData = {
      ...data,
      createdAt: now,
      updatedAt: now,
    };
    const noteDocRef = await addDoc(notesCollectionRef, noteData);
    return {
      id: noteDocRef.id,
      ...noteData,
    };
  }

  async updateLearnerNote(
    id: string,
    updates: UpdateLearnerNoteInput,
  ): Promise<void> {
    const noteDocRef = doc(this.db, 'learnerNotes', id);
    await updateDoc(noteDocRef, {
      ...updates,
      updatedAt: Date.now(),
    });
  }

  async deleteLearnerNote(id: string): Promise<void> {
    const noteDocRef = doc(this.db, 'learnerNotes', id);
    await deleteDoc(noteDocRef);
  }

  // Custom Level methods
  async getCustomLevelsForOrganisation(orgId: string): Promise<CustomLevel[]> {
    const customLevelsQuery = query(
      collection(this.db, 'customLevels'),
      where('organisationId', '==', orgId),
      orderBy('updatedAt', 'desc'),
    );
    const snapshot = await getDocs(customLevelsQuery);
    return snapshot.docs.map(
      (doc) =>
        ({
          id: doc.id,
          ...doc.data(),
        }) as CustomLevel,
    );
  }

  async createCustomLevel(data: CreateCustomLevelInput): Promise<CustomLevel> {
    const collectionRef = collection(this.db, 'customLevels');
    const now = Date.now();
    const levelData = {
      ...data,
      createdAt: now,
      updatedAt: now,
    };
    const docRef = await addDoc(collectionRef, levelData);
    return {
      id: docRef.id,
      ...levelData,
    };
  }

  async updateCustomLevel(
    id: string,
    updates: UpdateCustomLevelInput,
  ): Promise<void> {
    const docRef = doc(this.db, 'customLevels', id);
    await updateDoc(docRef, {
      ...updates,
      updatedAt: Date.now(),
    });
  }

  async updateCustomLevelStudentProgress(
    customLevelId: string,
    studentId: string,
    progress: CustomLevelStudentProgress,
  ): Promise<void> {
    const docRef = doc(this.db, 'customLevels', customLevelId);
    await updateDoc(docRef, {
      [`students.${studentId}`]: progress,
      updatedAt: Date.now(),
    });
  }

  async deleteCustomLevel(id: string): Promise<void> {
    const docRef = doc(this.db, 'customLevels', id);
    await deleteDoc(docRef);
  }

  async deletePlacementTest(placementTestId: string) {
    const placementTestDocRef = doc(this.db, 'placementTests', placementTestId);
    await deleteDoc(placementTestDocRef);

    // delete placment test results
    const placementTestResultsQuery = query(
      collection(this.db, 'placementTestResults'),
      where('placementTestId', '==', placementTestId),
    );
    const placementTestResultsSnapshot = await getDocs(
      placementTestResultsQuery,
    );
    for (const doc of placementTestResultsSnapshot.docs) {
      await deleteDoc(doc.ref);
    }
  }

  async getPlacementTestsAndResultsForOrganisation(
    organisationId: string,
  ): Promise<{
    placementTests: PlacementTest[];
    placementTestResults: PlacementTestResult[];
  }> {
    const placementTestsQuery = query(
      collection(this.db, 'placementTests'),
      where('organisationId', '==', organisationId),
    );
    const placementTestsSnapshot = await getDocs(placementTestsQuery);
    const placementTests = placementTestsSnapshot.docs.map(
      (doc) =>
        ({
          id: doc.id,
          ...doc.data(),
        }) as PlacementTest,
    );

    if (placementTests.length === 0) {
      return { placementTests: [], placementTestResults: [] };
    }

    const chunks = chunk(
      placementTests.map((test) => test.id),
      30,
    );

    const resultsSnapshots = await Promise.all(
      chunks.map((chunk) =>
        getDocs(
          query(
            collection(this.db, 'placementTestResults'),
            where('organisationId', '==', organisationId),
            where('placementTestId', 'in', chunk),
          ),
        ),
      ),
    );

    const placementTestResults = resultsSnapshots.flatMap((snapshot) =>
      snapshot.docs.map(
        (doc) =>
          ({
            id: doc.id,
            ...doc.data(),
          }) as PlacementTestResult,
      ),
    );

    return { placementTests, placementTestResults };
  }

  async getPlacementTestsForStudent(organisationId: string, studentId: string) {
    const placementTestsQuery = query(
      collection(this.db, 'placementTests'),
      where('organisationId', '==', organisationId),
      where('studentId', '==', studentId),
    );
    const placementTestsSnapshot = await getDocs(placementTestsQuery);
    return placementTestsSnapshot.docs.map(
      (doc) =>
        ({
          id: doc.id,
          ...doc.data(),
        }) as PlacementTest,
    );
  }

  async getPlacementTest(
    placementTestId: string,
  ): Promise<PlacementTest | undefined> {
    const placementTestDocRef = doc(this.db, 'placementTests', placementTestId);
    const placementTestDoc = await getDoc(placementTestDocRef);
    return placementTestDoc.data() as PlacementTest | undefined;
  }

  async markAssessmentStarted(assessmentId: string, studentId: string) {
    const assessmentDocRef = doc(this.db, 'assessments', assessmentId);

    const assessmentDoc = await getDoc(assessmentDocRef);
    if (!assessmentDoc.exists()) {
      console.error('Failed to mark assessment started: assessment not found');
      return;
    }
    const assessment = await migrateAssessment({
      id: assessmentDoc.id,
      ...assessmentDoc.data(),
    } as Assessment);

    if (!assessment.students[studentId]) {
      console.error(
        'Failed to mark assessment started: student not found in assessment',
      );
      return;
    }

    await updateDoc(assessmentDocRef, {
      students: {
        ...assessment.students,
        [studentId]: {
          ...assessment.students[studentId],
          startedAt: Date.now(),
        },
      },
    });
  }

  async markAssessmentCompleted(
    assessmentId: string,
    studentId: string,
    placementLevel: number,
  ) {
    const assessmentDocRef = doc(this.db, 'assessments', assessmentId);

    const assessmentDoc = await getDoc(assessmentDocRef);
    if (!assessmentDoc.exists()) {
      console.error('Failed to mark assessment complete: assessment not found');
      return;
    }
    const assessment = await migrateAssessment({
      id: assessmentDoc.id,
      ...assessmentDoc.data(),
    } as Assessment);

    if (!assessment.students[studentId]) {
      console.error(
        'Failed to mark assessment complete: student not found in assessment',
      );
      return;
    }

    await updateDoc(assessmentDocRef, {
      students: {
        ...assessment.students,
        [studentId]: {
          ...assessment.students[studentId],
          completedAt: Date.now(),
          placementLevel,
        },
      },
    });
  }

  // Student events
  async addStudentEvent(event: CreateStudentEvent) {
    const eventsCollectionRef = collection(this.db, 'events');
    const eventDocRef = await addDoc(eventsCollectionRef, {
      ...event,
      // so we don't have to specify null for every event
      nestedUnderSessionId: event.nestedUnderSessionId || null,
    });
    return eventDocRef.id;
  }

  async subscribeToEventsForStudent(
    organisationId: string,
    studentId: string,
    callback: (events: StudentEvent[]) => void,
  ) {
    const eventsQuery = query(
      collection(this.db, 'events'),
      where('organisationId', '==', organisationId),
      where('studentId', '==', studentId),
      where('nestedUnderSessionId', '==', null),
      orderBy('createdAt', 'desc'),
    );

    return onSnapshot(
      eventsQuery,
      (snapshot) =>
        callback(
          snapshot.docs.map(
            (doc) =>
              ({
                id: doc.id,
                ...doc.data(),
              }) as StudentEvent,
          ),
        ),
      (error) => {
        console.error('Error in events subscription:', error);
      },
    );
  }

  async getEventsForSession(organisationId: string, sessionId: string) {
    const eventsQuery = query(
      collection(this.db, 'events'),
      where('organisationId', '==', organisationId),
      where('nestedUnderSessionId', '==', sessionId),
      orderBy('createdAt', 'desc'),
    );

    const eventsSnapshot = await getDocs(eventsQuery);

    return eventsSnapshot.docs.map(
      (doc) =>
        ({
          id: doc.id,
          ...doc.data(),
        }) as StudentEvent,
    );
  }

  // Auth related functions
  async generateVerificationCode(params: { uid: string }): Promise<void> {
    const generateVerificationCodeFn = httpsCallable(
      this.functions,
      'generateVerificationCode',
    );
    await generateVerificationCodeFn(params);
  }

  async verifyEmailCode(params: { uid: string; code: string }): Promise<{
    success: boolean;
    verified: boolean;
    message: string;
  }> {
    const verifyEmailCodeFn = httpsCallable(this.functions, 'verifyEmailCode');
    const result = await verifyEmailCodeFn(params);
    return result.data as {
      success: boolean;
      verified: boolean;
      message: string;
    };
  }

  // Email related functions
  async sendWelcomeEmail(params: { uid: string }): Promise<void> {
    const sendWelcomeEmailFn = httpsCallable(
      this.functions,
      'sendWelcomeEmail',
    );
    await sendWelcomeEmailFn(params);
  }

  async triggerTrialEndHandler(): Promise<void> {
    const trialEndHandlerFn = httpsCallable(
      this.functions,
      'handleTrialExpired_dev',
    );
    await trialEndHandlerFn();
  }

  async sendInviteEmail(params: {
    organisationId: string;
    organisationName: string;
    email: string;
    inviterEmail: string;
    inviterName: string;
  }): Promise<void> {
    const sendInviteEmailFn = httpsCallable(this.functions, 'sendInviteEmail');
    await sendInviteEmailFn(params);
  }

  async sendSubscriptionConfirmedEmail(params: {
    uid: string;
    organisationName: string;
    planName: string;
    seats: number;
    amountDisplay: string;
    intervalDisplay: string;
    nextBillDateDisplay: string;
  }): Promise<void> {
    const sendSubscriptionConfirmedEmailFn = httpsCallable(
      this.functions,
      'sendSubscriptionConfirmedEmail',
    );
    await sendSubscriptionConfirmedEmailFn(params);
  }

  async sendContactFormEmail(params: {
    message: string;
    organisationName: string;
  }): Promise<void> {
    const sendContactFormEmailFn = httpsCallable(
      this.functions,
      'sendContactFormEmail',
    );
    await sendContactFormEmailFn(params);
  }

  // Stripe/Payment related functions
  async createStripeCustomer(params: { email: string; name: string }): Promise<{
    stripeCustomerId: string;
  }> {
    const createStripeCustomerFn = httpsCallable<
      { email: string; name: string },
      { stripeCustomerId: string }
    >(this.functions, 'createStripeCustomer');
    const result = await createStripeCustomerFn(params);
    return result.data;
  }

  async createSubscription(params: {
    customerId: string;
    priceId: string;
    seatCount: number;
    couponId?: string;
  }): Promise<{ clientSecret: string }> {
    const createSubscriptionFn = httpsCallable(
      this.functions,
      'createSubscription',
    );
    const result = await createSubscriptionFn(params);
    return result.data as { clientSecret: string };
  }

  async cancelSubscription(params: {
    customerId: string;
    subscriptionId: string | null;
  }): Promise<void> {
    const cancelSubscriptionFn = httpsCallable(
      this.functions,
      'cancelSubscription',
    );
    await cancelSubscriptionFn(params);
  }

  async updateBillingAddress(params: {
    customerId: string;
    billingInfo: BillingInfo;
  }): Promise<void> {
    const updateBillingAddressFn = httpsCallable(
      this.functions,
      'updateBillingAddress',
    );
    await updateBillingAddressFn(params);
  }

  async getCouponFromCode(params: { code: string }): Promise<Coupon | null> {
    const getCouponFromCodeFn = httpsCallable(
      this.functions,
      'getCouponFromCode',
    );
    const result = await getCouponFromCodeFn(params);
    return result.data as Coupon | null;
  }

  async summarizeConfirmationTokenDetails(params: {
    confirmationTokenId: string;
  }): Promise<BillingInfo> {
    const summarizeConfirmationTokenDetailsFn = httpsCallable(
      this.functions,
      'summarizeConfirmationTokenDetails',
    );
    const result = await summarizeConfirmationTokenDetailsFn(params);
    return result.data as BillingInfo;
  }

  async summarizePaymentMethod(params: {
    paymentMethodId: string;
  }): Promise<BillingInfo> {
    const summarizePaymentMethodFn = httpsCallable(
      this.functions,
      'summarizePaymentMethod',
    );
    const result = await summarizePaymentMethodFn(params);
    return result.data as BillingInfo;
  }

  async confirmInvoicePayment(params: {
    organisationId: string;
    licenseUpdates: Partial<OrganisationLicense>;
  }): Promise<void> {
    const confirmInvoicePaymentFn = httpsCallable(
      this.functions,
      'confirmInvoicePayment',
    );
    await confirmInvoicePaymentFn(params);
  }

  // Auth functions
  async getStudentAuthToken(params: {
    username: string;
    password: string;
  }): Promise<{ token: string }> {
    const getStudentAuthTokenFn = httpsCallable(
      this.functions,
      'getStudentAuthToken',
    );
    const result = await getStudentAuthTokenFn(params);
    return result.data as { token: string };
  }

  async syncUserClaims(): Promise<void> {
    const syncUserClaimsFn = httpsCallable(this.functions, 'syncUserClaims');
    return void syncUserClaimsFn();
  }

  // Bulk student operations
  async bulkAddStudents(params: {
    students: Omit<Student, 'id'>[];
    organisationId: string;
  }): Promise<{ studentIds: string[] }> {
    const bulkAddStudentsFn = httpsCallable(this.functions, 'bulkAddStudents');
    const result = await bulkAddStudentsFn(params);
    return result.data as { studentIds: string[] };
  }

  async bulkDeleteStudents(params: {
    studentIds: string[];
  }): Promise<{ deletedAssessmentIds: string[] }> {
    const bulkDeleteStudentsFn = httpsCallable(
      this.functions,
      'bulkDeleteStudents',
    );
    const result = await bulkDeleteStudentsFn(params);
    return result.data as { deletedAssessmentIds: string[] };
  }

  async bulkUpdateStudentSettings(params: {
    studentIds: string[];
    userName: string;
    organisationId: string;
    settings: GameSettings;
    location: string;
  }): Promise<void> {
    const bulkUpdateStudentSettingsFn = httpsCallable(
      this.functions,
      'bulkUpdateStudentSettings',
    );
    await bulkUpdateStudentSettingsFn(params);
  }

  async bulkAssignStudentAssessments(params: {
    studentIds: string[];
    assessmentId: string;
  }): Promise<void> {
    const bulkAssignStudentAssessmentsFn = httpsCallable(
      this.functions,
      'bulkAssignStudentAssessments',
    );
    await bulkAssignStudentAssessmentsFn(params);
  }

  async bulkUpdateStudents(params: {
    organisationId: string;
    updates: Array<{
      studentId: string;
      password?: string;
      spellcastUsername?: string;
      level?: number;
      levelsProgress?: LevelProgress[];
    }>;
  }): Promise<void> {
    const fn = httpsCallable(this.functions, 'bulkUpdateStudents');
    await fn(params);
  }

  async generateStudentUsernames(params: {
    names: { firstName: string; lastName: string }[];
  }): Promise<{ usernames: string[] }> {
    const fn = httpsCallable(this.functions, 'generateStudentUsernames');
    const result = await fn(params);
    return result.data as { usernames: string[] };
  }
}

const asEducator = new FirebaseService('dashboard');
const asStudent = new FirebaseService('game');

// Created lazily on first share-link visit; kept alive to avoid duplicate-app
// errors on repeat visits. Guarded by _shareLinkActive for asShareLink access.
let _shareLinkService: FirebaseService | null = null;
let _shareLinkActive = false;

const api = {
  asEducator,
  asStudent,

  /** The share-link–scoped Firebase service, or null when not active. */
  get asShareLink(): FirebaseService | null {
    return _shareLinkActive ? _shareLinkService : null;
  },

  /**
   * Exchanges a share link ID for a Firebase custom auth token (via CF),
   * then signs into the share-link Firebase service with that token.
   * Returns the resolved shareLink + student, or null if the link is
   * missing/expired.
   */
  async initShareLink(
    shareLinkId: string,
  ): Promise<{ shareLink: ShareLink; student: Student } | null> {
    const fn = httpsCallable<
      { shareLinkId: string },
      { customToken: string; shareLink: ShareLink; student: Student } | null
    >(asEducator.functions, 'getShareLinkAuthToken');
    const result = await fn({ shareLinkId });
    if (!result.data) return null;

    const { customToken, shareLink, student } = result.data;

    if (!_shareLinkService) {
      _shareLinkService = new FirebaseService('shareLink');
    }
    await signInWithCustomToken(_shareLinkService.auth, customToken);
    _shareLinkActive = true;

    return { shareLink, student };
  },

  /** Signs out the share-link session. Call on component unmount. */
  async signOutShareLink(): Promise<void> {
    _shareLinkActive = false;
    if (_shareLinkService) {
      await signOut(_shareLinkService.auth);
    }
  },
};

export default api;
